import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/redux/store/store';
import { getCompanyRules } from '../api/General/Company';
import { currencyDisplay } from '../utils/currencyDisplay';

interface CurrencyInfo {
  /** The tenant's ISO currency code, or `''`. */
  code: string;
  /**
   * CO-804 — the string to render next to an amount: **code first**, symbol only as a last
   * resort. Never read `currencySymbol` here; see `utils/currencyDisplay.ts`.
   */
  display: string;
  format: (amount: number | string, decimals?: number) => string;
}

export function useCurrency(): CurrencyInfo {
  const companyRulesCurrency = useSelector(
    (state: RootState) => state.user.company?.companyRules?.currency
  );
  const companyId = useSelector(
    (state: RootState) => state.user.company?.id
  );

  const [fetched, setFetched] = useState<{ currencyCode?: string; currencySymbol?: string } | null>(null);

  // If currency is not in Redux state, fetch from API
  useEffect(() => {
    if (!companyRulesCurrency && companyId && !fetched) {
      getCompanyRules(companyId)
        .then((result) => {
          if (result.errors.length === 0 && result.payload) {
            setFetched({
              currencyCode: (result.payload as any).currency?.currencyCode,
              currencySymbol: (result.payload as any).currency?.currencySymbol,
            });
          }
        })
        .catch(() => {});
    }
  }, [companyRulesCurrency, companyId, fetched]);

  const currency = companyRulesCurrency || fetched;
  const code = currency?.currencyCode?.trim() || '';
  // CO-804: code first. The previous `symbol ? ... : code ? ...` ordering put an Arabic-script
  // symbol in front of the digits, which reordered them into the symbol's RTL run.
  const display = currencyDisplay(currency);

  const format = (amount: number | string, decimals = 2): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return display ? `${display} 0.${'0'.repeat(decimals)}` : `0.${'0'.repeat(decimals)}`;
    const formatted = num.toFixed(decimals);
    return display ? `${display} ${formatted}` : formatted;
  };

  return { code, display, format };
}
